/**
 * emamaco gift card link handler + landing confirmation.
 *
 * A campaign email or SMS lands the buyer HERE, on the store — not on checkout. A gift card cannot
 * be applied on the storefront at all (the AJAX cart has no gift-card concept; it is a
 * checkout-level payment instrument, which is why Shopify hides it in the checkout
 * Discount/Promo Code accordion). So this does the only thing that works:
 *
 *   1. read the per-recipient token from ?gcect=
 *   2. exchange it for that person's own gift card code over XHR
 *   3. write the code into the cart attribute `emamaco_gift_card`
 *   4. scrub the token out of the URL
 *   5. tell the buyer what just happened, because nothing on the page looks any different
 *
 * Step 5 is the whole reason anyone trusts step 2. Without it the buyer clicks "you have $25" and
 * arrives at an ordinary shop page with no money visible anywhere, and the only confirmation comes
 * three pages later at checkout.
 *
 * ALSO (2026-10-13):
 *   - the card is kept in this browser (cm_ad_card), exactly as the ad pages do, so the product page's
 *     price ladder, the cart and the "your card is on" bar show it (window.cmHeldCard reads it). Before
 *     this, a card from an email applied at checkout but showed nowhere on the site.
 *   - ?gch= (sha256 of the code) arrives from Shopify's OWN gift card email, through the theme's gift
 *     card page (templates/gift_card.liquid): that email is opened far more than ours, so it applies the
 *     card too. A gch we do not know (a bought card, say) is a quiet no-op: the page she came from shows
 *     her code.
 *   - her email hash goes in cm_emamaco_email_hash, the cookie the site's funnel tracker already reads,
 *     so what she does next on the site is hers (we send without open or click tracking).
 *
 * WHY THE CODE IS NEVER IN THE URL: a code is a bearer instrument. In a query string it would land
 * in browser history, in Referer headers, and in any analytics script that logs URLs. The token in
 * the URL is worthless on its own.
 *
 * NO EMOJIS anywhere in this file, per the house style.
 */
(function () {
  var PARAM = 'gcect';
  var HASH_PARAM = 'gch';
  var ATTR = 'emamaco_gift_card';
  var API = 'https://api.convomesh.com/giftcard/emamaco/claim';
  var RESEND_API = 'https://api.convomesh.com/giftcard/emamaco/resend';
  var DISMISS_KEY = 'emamaco_gc_notice_seen';

  // ── helpers ────────────────────────────────────────────────────────────────────────────
  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n) || n <= 0) return '';
    var d = n / 100;
    // Whole dollars read better without the cents, and AUD is the only currency this store
    // bills in — so a plain '$' is unambiguous here and avoids Intl's narrowSymbol quirk,
    // which renders AUD and USD identically anyway.
    return '$' + (d % 1 === 0 ? String(d) : d.toFixed(2));
  }

  function prettyDate(iso) {
    // YYYY-MM-DD parsed by hand. `new Date('2026-09-30')` is parsed as UTC midnight and then
    // printed in local time, which in Australia renders the PREVIOUS day — the same off-by-one
    // that had the send copy advertising a card as dead a day early.
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!m) return '';
    var d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (e) {
      return m[3] + '/' + m[2];
    }
  }

  function scrubUrl() {
    try {
      var u = new URL(window.location.href);
      if (!u.searchParams.has(PARAM) && !u.searchParams.has(HASH_PARAM)) return;
      u.searchParams.delete(PARAM);
      u.searchParams.delete(HASH_PARAM);
      window.history.replaceState({}, '', u.pathname + (u.search || '') + (u.hash || ''));
    } catch (e) { /* history is a nicety, never worth throwing over */ }
  }

  function alreadyStashed(code) {
    // /cart.js is cheap and cached by the browser for the tab; skip the write when the same code
    // is already on the cart so a refresh does not churn the cart object.
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return !!(c && c.attributes && c.attributes[ATTR] === code); })
      .catch(function () { return false; });
  }

  function stash(code) {
    var body = {};
    body[ATTR] = code;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: body }),
    }).then(function (r) {
      // The product page's take box offers a gift card for a video opinion, but not to someone who already holds
      // one of ours (Mark, 2026-10-10): tell it the card is on now, so a page already showing re-checks.
      if (r.ok) { try { window.dispatchEvent(new CustomEvent('emamaco:gift-card-on')); } catch (e) {} }
      return r.ok;
    });
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    try { return m ? decodeURIComponent(m[1]) : ''; } catch (e) { return ''; }
  }

  // The card in this browser, the way the ad pages keep theirs: the product page, the cart and the
  // top bar all read it (window.cmHeldCard), and the bar re-checks it is live once a visit by `g`.
  function remember(d) {
    try {
      localStorage.setItem('cm_ad_card', JSON.stringify({
        g: d.g || '', code: String(d.code).toUpperCase(), last4: d.last4 || null,
        amount_cents: d.amountCents, expires_on: d.expiresOn || null,
      }));
      sessionStorage.setItem('cm_ad_card_checked', JSON.stringify({ live: true, cents: d.amountCents }));
      sessionStorage.removeItem('cm_ad_card_hidden');
    } catch (e) { /* private mode: checkout still applies the card */ }
    if (d.emailHash && /^[0-9a-f]{64}$/.test(d.emailHash) && cookie('cm_emamaco_email_hash') !== d.emailHash) {
      try {
        document.cookie = 'cm_emamaco_email_hash=' + d.emailHash + '; expires=' +
          new Date(Date.now() + 730 * 864e5).toUTCString() + '; path=/; SameSite=Lax' +
          (location.protocol === 'https:' ? '; Secure' : '');
      } catch (e) {}
    }
    try { window.dispatchEvent(new CustomEvent('cm:card-checked', { detail: { live: true, cents: d.amountCents } })); } catch (e) {}
  }

  // ── the notice ─────────────────────────────────────────────────────────────────────────
  var STYLE = [
    '.emgc-back{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;',
    'justify-content:center;padding:20px;background:rgba(17,24,39,.55);opacity:0;',
    'transition:opacity .18s ease}',
    '.emgc-back.emgc-in{opacity:1}',
    '.emgc-card:focus{outline:none}',
    '.emgc-card{position:relative;width:100%;max-width:380px;background:#fff;border-radius:14px;',
    'padding:26px 24px 22px;box-shadow:0 18px 50px rgba(0,0,0,.28);text-align:center;',
    'font-family:inherit;color:#1f2937;transform:translateY(8px) scale(.98);',
    'transition:transform .18s ease}',
    '.emgc-back.emgc-in .emgc-card{transform:none}',
    '.emgc-x{position:absolute;top:8px;right:10px;width:34px;height:34px;border:0;',
    'background:none;font-size:22px;line-height:1;color:#9ca3af;cursor:pointer;border-radius:8px}',
    '.emgc-x:hover{color:#1f2937;background:#f3f4f6}',
    '.emgc-amt{font-size:38px;font-weight:700;letter-spacing:-.02em;margin:2px 0 4px}',
    '.emgc-h{font-size:17px;font-weight:600;margin:0 0 8px}',
    '.emgc-p{font-size:14px;line-height:1.5;margin:0 0 6px;color:#4b5563}',
    '.emgc-note{font-size:12px;line-height:1.5;margin:14px 0 0;color:#6b7280}',
    '.emgc-btn{display:block;width:100%;margin:18px 0 0;padding:13px 18px;background:#1f2937;',
    'color:#fff;border:0;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;',
    'font-family:inherit}',
    '.emgc-btn:hover{background:#111827}',
    '.emgc-btn2{display:block;width:100%;margin:8px 0 0;padding:12px 18px;background:#fff;',
    'color:#1f2937;border:1px solid #d1d5db;border-radius:8px;font-size:14px;font-weight:600;',
    'cursor:pointer;font-family:inherit}',
    '.emgc-btn2:hover{background:#f9fafb}',
    '.emgc-btn2[disabled]{opacity:.55;cursor:default}',
    '.emgc-said{font-size:13px;line-height:1.5;margin:10px 0 0;color:#166534;font-weight:600}',
    '@media (prefers-reduced-motion:reduce){.emgc-back,.emgc-card{transition:none}}',
  ].join('');

  var open = false;

  function notice(kind, data, token) {
    if (open) return;
    // One notice per visit. A buyer who dismissed it and kept shopping must not meet it again on
    // the next page, and sessionStorage is the only store that clears itself when they leave.
    try { if (sessionStorage.getItem(DISMISS_KEY)) return; } catch (e) { /* private mode */ }
    open = true;
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* private mode */ }

    if (!document.getElementById('emgc-style')) {
      var st = document.createElement('style');
      st.id = 'emgc-style';
      st.textContent = STYLE;
      document.head.appendChild(st);
    }

    var amount = money(data && data.amountCents);
    var when = prettyDate(data && data.expiresOn);
    var last4 = data && data.last4 ? String(data.last4) : '';

    var head, para, note;
    if (kind === 'applied') {
      head = 'Your gift card is on';
      para = 'It comes off automatically at checkout' +
             (last4 ? ', and you will see it there as the card ending ' + last4 : '') + '.';
      note = when
        ? 'Use it by ' + when + '. If anything looks wrong at checkout, your code is in your email from emamaco.'
        : 'If anything looks wrong at checkout, your code is in your email from emamaco.';
    } else {
      // Either we hold no code for this person (they already had a card of their own, so we never
      // knew its number), or the exchange failed. Both land here, because the buyer's next step is
      // identical and they do not care which it was.
      head = 'Your gift card is waiting';
      para = 'We could not attach it to this visit automatically.';
      note = 'Your code is in your email from emamaco, with the subject "emamaco gift card". ' +
             'Enter it at checkout under "Use emamaco gift card" - or have it sent again below.';
    }

    var back = document.createElement('div');
    back.className = 'emgc-back';
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', 'Gift card');

    var card = document.createElement('div');
    card.className = 'emgc-card';
    // Focus lands on the panel, not on the primary button: focusing the button paints a focus
    // ring that reads as "already selected" on first sight. tabindex -1 keeps it focusable for
    // screen readers and keeps Escape and Tab working without that.
    card.setAttribute('tabindex', '-1');

    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'emgc-x';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML = '&times;';

    // Amount only when we actually know it — an empty or wrong figure is worse than none.
    if (kind === 'applied' && amount) {
      var amt = document.createElement('div');
      amt.className = 'emgc-amt';
      amt.textContent = amount;
      card.appendChild(amt);
    }

    var h = document.createElement('p'); h.className = 'emgc-h';    h.textContent = head;
    var p = document.createElement('p'); p.className = 'emgc-p';    p.textContent = para;
    var n = document.createElement('p'); n.className = 'emgc-note'; n.textContent = note;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'emgc-btn';
    btn.textContent = 'Start shopping';

    card.appendChild(x); card.appendChild(h); card.appendChild(p); card.appendChild(n);

    // Self-serve resend. Shopify mails the card to the address on its own customer record, so this
    // button cannot leak a card to whoever is holding the link — and it is the ONLY route for
    // someone whose card we never held the code for, who would otherwise have to email support.
    // Needs the token, so it is absent when there is nothing to authorise with.
    if (token) {
      var resend = document.createElement('button');
      resend.type = 'button';
      resend.className = 'emgc-btn2';
      resend.textContent = 'Email me my gift card';
      var said = document.createElement('p');
      said.className = 'emgc-said';
      said.style.display = 'none';
      resend.addEventListener('click', function () {
        resend.disabled = true;
        resend.textContent = 'Sending...';
        fetch(RESEND_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ect: token }),
        })
          .then(function (r) { return r.json().catch(function () { return null; }); })
          .then(function (out) {
            // ok:true with sent:false is the cooldown or an unknown token. Both mean "it is on its
            // way or already was", and saying so is true — a second tap does not need a new email.
            if (out && out.ok) {
              resend.style.display = 'none';
              said.textContent = 'Sent. Check your email in a minute, including junk.';
              said.style.display = 'block';
            } else if (out && out.error === 'card_ended') {
              // The card has ended: it cannot be sent again, and "Try again" would fail every time.
              var ended = prettyDate(out.ended_on);
              resend.style.display = 'none';
              said.textContent = 'This gift card ended' + (ended ? ' on ' + ended : '') +
                '. Reply to the email it came in to ask about more time.';
              said.style.display = 'block';
            } else {
              resend.disabled = false;
              resend.textContent = 'Try again';
            }
          })
          .catch(function () { resend.disabled = false; resend.textContent = 'Try again'; });
      });
      card.appendChild(resend);
      card.appendChild(said);
    }

    card.appendChild(btn);
    back.appendChild(card);
    document.body.appendChild(back);
    requestAnimationFrame(function () { back.classList.add('emgc-in'); });

    var lastFocus = document.activeElement;
    function close() {
      back.classList.remove('emgc-in');
      document.removeEventListener('keydown', onKey);
      setTimeout(function () {
        if (back.parentNode) back.parentNode.removeChild(back);
        open = false;
        try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (e) {}
      }, 200);
    }
    function onKey(e) {
      if (e.key === 'Escape' || e.keyCode === 27) close();
      // Focus stays inside while it is open: two focusables, so a manual wrap is enough.
      if (e.key === 'Tab') {
        e.preventDefault();
        (document.activeElement === btn ? x : btn).focus();
      }
    }
    x.addEventListener('click', close);
    btn.addEventListener('click', close);
    back.addEventListener('click', function (e) { if (e.target === back) close(); });
    document.addEventListener('keydown', onKey);
    try { card.focus(); } catch (e) {}
  }

  // ── run ────────────────────────────────────────────────────────────────────────────────
  function run() {
    var token, hash;
    try {
      var q = new URL(window.location.href).searchParams;
      token = q.get(PARAM);
      hash = token ? null : q.get(HASH_PARAM);
    } catch (e) { return; }
    if (!token && !(hash && /^[0-9a-f]{64}$/i.test(hash))) return;

    var body = token ? { ect: token } : { gch: String(hash).toLowerCase() };
    var vid = cookie('cm_emamaco_vid');
    if (vid) body.vid = vid;
    // A gch we cannot place is a quiet no-op (the gift card page she came from shows her code); a
    // campaign link that fails tells her where her code is.
    var miss = function () { scrubUrl(); if (token) notice('manual', null, token); };

    fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.ok || !d.code) { miss(); return null; }
        return alreadyStashed(d.code)
          .then(function (have) { return have ? true : stash(d.code); })
          .then(function (stashed) {
            scrubUrl();
            if (stashed) remember(d);
            // Let the theme show its own confirmation too if it wants one. No code in the event.
            try {
              window.dispatchEvent(new CustomEvent('emamaco:giftcard:ready', {
                detail: { last4: d.last4, amountCents: d.amountCents, expiresOn: d.expiresOn },
              }));
            } catch (e) { /* older browsers */ }
            // A failed cart write means checkout will NOT have the card, so the buyer needs the
            // manual route — telling them it is on would be a lie they discover at payment.
            notice(stashed ? 'applied' : 'manual', d, token);
            return true;
          });
      })
      .catch(miss);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
